/* eslint-disable jsx-a11y/label-has-associated-control */
import { createUseStyles, useDarkMode } from '@glu/theming';
import PropTypes from 'prop-types';
import React, {
  useState, useRef, useEffect, useMemo
} from 'react';
import { useClickOutside } from '@glu/utilities-react';
import { withInputAPI } from '@glu/validation-react';
import { LabelText, Error } from '@glu/form-components';
import locale from '@glu/locale';
import Panel from './Panel';
import SelectField from './SelectField';
import Tooltip from './Tooltip';
import styles from './Dropdown.styles';
import { getPreSelected, getPreSelectedFormData } from './utils/getPreSelected';
import './themeDefaults';

const useStyles = createUseStyles(styles);

const Dropdown = ({
  className,
  dataQa,
  disabled,
  emptyOptionsMessage,
  errors,
  filterBy,
  filterDebounce,
  fitParentWidth,
  format,
  htmlId,
  isLoading,
  labelText,
  minSelectedForTooltip,
  multiselect,
  name,
  onBlur,
  onChange,
  onClose,
  onDone,
  onFirstOpen,
  screenReaderLabel,
  showGroupInSelection,
  showTooltip,
  value: selectedOptions,
  forceOpen,
  ...rest
}) => {
  const dark = useDarkMode();
  const classes = useStyles();
  const [error] = errors;

  const [focusedIndex, setFocusedIndex] = useState(undefined);
  const [isFilterFocused, setIsFilterFocused] = useState(true);
  const [done, setDone] = useState(false);
  const [open, setOpen] = useState(forceOpen);
  const [pressedKey, setPressedKey] = useState(undefined);
  const [selection, setSelection] = useState(getPreSelected(rest));
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [wasOpened, setWasOpened] = useState(false);
  const prevSelectionRef = useRef(selection);

  useEffect(() => {
    if (selectedOptions) {
      const [parsedData, isMutated] = getPreSelectedFormData(selectedOptions, rest.options);
      setSelection(parsedData);
      if (isMutated && onChange) {
        // update form context with full data object
        onChange(name, parsedData, true);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, selectedOptions, rest.options]);

  useEffect(() => {
    setOpen(forceOpen);
  }, [forceOpen]);

  // multiselects automatically apply changes, so fire onChange event whenever selection changes
  useEffect(() => {
    if (multiselect && JSON.stringify(prevSelectionRef.current) !== JSON.stringify(selection)) {
      prevSelectionRef.current = selection;
      onChange(name, selection, true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection, multiselect, name]);

  const openDropdown = (value) => {
    setDone(false);
    if (onFirstOpen && !wasOpened && value) {
      setWasOpened(true);
      onFirstOpen();
    }
    setOpen(value);
  };

  const closeDropdown = () => {
    if (open) {
      onBlur();
    }
    onClose();
    setFocusedIndex(undefined);
    openDropdown(forceOpen);
    setTooltipOpen(false);
    setIsFilterFocused(true);
  };

  const applySelection = (value) => {
    closeDropdown();
    if (value !== selection) {
      setSelection(value);
      onChange(name, value, true);
    }
    onDone(name, value);
    setDone(true);
  };

  const handleClose = () => {
    if (open) {
      closeDropdown();
      applySelection(selection);
    }
  };

  /** useClickOutside runs at the document, so esc needs captured earlier to avoid
   ** other modules like modals from catching it and closing */
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      handleClose();
    }
  };

  const ref = useRef();
  useClickOutside(ref, handleClose);

  const uniqueId = useMemo(() => (
    htmlId || `dropdown-${(Math.random() * 100000).toFixed()}`
  ), [htmlId]);

  return (
    <div // eslint-disable-line jsx-a11y/no-static-element-interactions
      className={`${classes.root} ${className}`}
      data-qa={dataQa ? `${dataQa}` : `dropdown-${htmlId}`}
      onMouseEnter={() => { if (showTooltip) setTooltipOpen(true); }}
      onMouseLeave={() => { if (showTooltip) setTooltipOpen(false); }}
      onKeyDown={handleKeyDown}
      ref={ref}
    >
      <label id={`${uniqueId}-select-field-label`} htmlFor={`${uniqueId}-select-field`}>
        <LabelText
          dark={dark}
          screenReaderLabel={screenReaderLabel}
          label={labelText}
          hasError={!!error}
        />
      </label>
      <div className={classes.wrapper} data-disabled={disabled}>
        <SelectField
          {...{
            applySelection,
            className,
            dark,
            dataQa,
            disabled,
            done,
            fitParentWidth,
            format,
            handleClose,
            htmlId: uniqueId,
            multiselect,
            name,
            onBlur,
            onClose,
            open,
            openDropdown,
            selection,
            setFocusedIndex,
            setPressedKey,
            setSelection,
            showGroupInSelection,
            ...rest
          }}
        />
        {open && (
        <Panel
          {...{
            applySelection,
            className,
            emptyOptionsMessage: emptyOptionsMessage || locale.get('noResultsFound'),
            dark,
            dataQa,
            filterBy,
            filterDebounce,
            focusedIndex,
            format,
            htmlId: uniqueId,
            isFilterFocused,
            isLoading,
            multiselect,
            name,
            onBlur,
            onClose,
            onChange,
            pressedKey,
            selection,
            setFocusedIndex,
            setIsFilterFocused,
            screenReaderLabel,
            setPressedKey,
            setSelection,
            ...rest
          }}
        />
        )}
        {selection.length > 0
          && showTooltip
          && minSelectedForTooltip <= selection.length
          && tooltipOpen
          && !open
          && <Tooltip selection={selection} format={format} />}
      </div>
      <Error dark={dark} error={error} />
    </div>
  );
};

Dropdown.propTypes = {
  /** Toggle "Clear" button to clear current selection */
  btnClearEnabled: PropTypes.bool,

  /** Toggle if done button is visible. Actively selecting done is not required. */
  btnDoneEnabled: PropTypes.bool,

  /** Toggle "Select All" button to select all avaialbe rows at the current level */
  btnSelectAllEnabled: PropTypes.bool,

  /** Class passed to parent element for extra decoration. */
  className: PropTypes.string,

  /** Data object used to generate options
   * expected format:
   * [{id: 123, name: 'foobar', child: array}] */
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    name: PropTypes.string.isRequired
  })),

  /** String given to parent elements data-qa html attribute for easy targeting */
  dataQa: PropTypes.string,

  /** Disable the dropdown entirely */
  disabled: PropTypes.bool,

  /** Custom message to show in the select when there are no options to display */
  emptyOptionsMessage: PropTypes.string,

  /** Error messages. Will add styles to input if present. */
  errors: PropTypes.arrayOf(PropTypes.string),

  /* String defining what filtering algoritm to use.
   * Defaults to "contains" if none provided */
  filterBy: PropTypes.oneOf(['contains', 'startsWith']),

  /**
   * Function to call when filtering is desired. It will be passed a function that,
   * when called will perform the actions of filtering the list.
   * Useful for creating a custom debounce function
   */
  filterDebounce: PropTypes.func,

  /** Whether to allow filtering by text input */
  filterEnabled: PropTypes.bool,

  /** Placeholder text to display in filter input */
  filterPlaceholder: PropTypes.string,

  /**
   * UX setting to make the dropdown fit the parent element's width.
   * If the selected items do not fit within the dropdown's width they will
   * be wrapped to new lines.
   */
  fitParentWidth: PropTypes.bool,

  /** Function receives selection array as a parameter and is expected to return a string.
   * Used for changing the format of the string value in the text input. */
  format: PropTypes.func,

  /** string assigned to HTML id attribute of component */
  htmlId: PropTypes.string,

  /** Boolean to show loading spinner */
  isLoading: PropTypes.bool,

  /** String used for label value */
  labelText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

  /** What properties to match on when filtering */
  matchBy: PropTypes.arrayOf(PropTypes.string),

  /** Number of minimum selected items to show tooltip */
  minSelectedForTooltip: PropTypes.number,

  /** Allow multiple selections of dropdown options */
  multiselect: PropTypes.bool,

  /** string passed to html name attribute of text input */
  name: PropTypes.string,

  /** Callback when a category is selected */
  onCategorySelect: PropTypes.func,

  /** Callback when "Clear" button clicked */
  onClear: PropTypes.func,

  /** Callback when the panel is closed. */
  onClose: PropTypes.func,

  /** Callback when user inputs new filter value */
  onFilter: PropTypes.func,

  /** Callback that is called when the user opens the dropdown for the first time */
  onFirstOpen: PropTypes.func,

  /** Callback when the next page of options are requested (usually after scrolling) */
  onNextPage: PropTypes.func,

  /** Callback when a date is selected or changed */
  onChange: PropTypes.func,

  /** Callback when "Select All" button clicked */
  onSelectAll: PropTypes.func,

  /** React component which will render the option item */
  optionComponent: PropTypes.func,

  /** React component which will render all the options */
  optionListComponent: PropTypes.func,

  /** Placeholder text for the select button when no selection has been made */
  placeholder: PropTypes.string,

  /** Array of option ids to include in selection */
  preSelectedIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),

  /** Array of option names to include in selection */
  preSelectedNames: PropTypes.arrayOf(PropTypes.string),

  /** First option is selected.
   * Overrides preSelectedIds and preSelectedNames. */
  preSelectFirstOption: PropTypes.bool,

  /** Toggle to hide the label for all but screenreaders */
  screenReaderLabel: PropTypes.bool,

  /** Toggle if selected items should display as badges */
  showBadges: PropTypes.bool,

  /** Toggle to display child elements as a flat list with parents as headers */
  showGroups: PropTypes.bool,

  /** Display the group of the selected value in the input. Only available for single select */
  showGroupInSelection: PropTypes.bool,

  /** Number of options to show in the select field before fallback to "N selected" message* */
  showSelected: PropTypes.number,

  /** Toggle display of tooltip which appears on hover */
  showTooltip: PropTypes.bool,

  /** Callback when panel is closed after being open */
  onBlur: PropTypes.func,

  /** Callback for when "Done" is clicked */
  onDone: PropTypes.func,

  /** Options which will be selected when component loads.
   * Overrides other preselect settings  */
  value: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
  })),

  /** used to control the open state by another higher component */
  forceOpen: PropTypes.bool
};

Dropdown.defaultProps = {
  btnClearEnabled: false,
  btnDoneEnabled: true,
  btnSelectAllEnabled: false,
  className: '',
  dataQa: '',
  disabled: false,
  emptyOptionsMessage: null,
  errors: [],
  filterBy: 'contains',
  filterDebounce: undefined,
  filterEnabled: true,
  filterPlaceholder: undefined,
  fitParentWidth: false,
  options: [],
  format: /* istanbul ignore next */ value => {
    if (value.name) return value.name;
    return value
      .map(curr => curr.name)
      .sort()
      .reduce((acc, curr) => (acc ? `${acc}, ${curr}` : curr), '');
  },
  htmlId: '',
  isLoading: false,
  labelText: '',
  matchBy: ['name'],
  multiselect: false,
  name: '',
  minSelectedForTooltip: 1,
  onBlur: /* istanbul ignore next */ () => {},
  onCategorySelect: undefined,
  onClear: /* istanbul ignore next */ () => {},
  onClose: /* istanbul ignore next */ () => {},
  onFilter: /* istanbul ignore next */ () => {},
  onFirstOpen: /* istanbul ignore next */ () => {},
  onNextPage: /* istanbul ignore next */ () => {},
  onChange: /* istanbul ignore next */ () => {},
  onDone: /* istanbul ignore next */ () => {},
  onSelectAll: /* istanbul ignore next */ () => {},
  optionComponent: undefined,
  optionListComponent: undefined,
  placeholder: 'Select',
  preSelectedIds: undefined, // deprecated, use value instead
  preSelectedNames: undefined, // deprecated, use value instead
  preSelectFirstOption: false, // deprecated, use value instead
  screenReaderLabel: false,
  showBadges: false,
  showGroups: false,
  showGroupInSelection: false,
  showSelected: 5,
  showTooltip: false,
  value: undefined,
  forceOpen: false
};

const InputApiDropdown = withInputAPI(Dropdown);
InputApiDropdown.displayName = 'Dropdown';
export default InputApiDropdown;
export const BareDropdown = Dropdown;
